import { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, CalendarDays } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAgreementStore } from '@/modules/agreements/store/agreementStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { usePunchcardCollects } from '../hooks/usePunchcardCollects';
import { useCollectsByDate } from '../hooks/useCollectsByDate';
import { useMonthlyStats } from '../hooks/useMonthlyStats';
import { useAgreementInfo } from '../hooks/useAgreementInfo';
import { DayTimelineCard } from '../components/DayTimelineCard';
import { CollectDetailDialog } from '../components/CollectDetailDialog';
import type { Collect } from '../types';

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function formatDayTotal(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}min`;
}

function calcDayTotalLabel(collects: Collect[]): string | undefined {
  if (collects.length === 0 || collects.length % 2 !== 0) return undefined;
  const sorted = [...collects].sort((a, b) => a.time.localeCompare(b.time));
  let total = 0;
  for (let i = 0; i + 1 < sorted.length; i += 2) {
    const e = sorted[i];
    const x = sorted[i + 1];
    if (e && x) {
      const diff = timeToMinutes(x.time) - timeToMinutes(e.time);
      if (diff > 0) total += diff;
    }
  }
  return formatDayTotal(total);
}

function getDayLabel(date: string): string {
  return format(parseISO(`${date}T12:00:00`), 'EEE', { locale: ptBR })
    .replace('.', '')
    .toUpperCase()
    .substring(0, 3);
}

function getDateLabel(date: string): string {
  return format(parseISO(`${date}T12:00:00`), 'dd/MM');
}

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

interface MonthPickerDialogProps {
  open: boolean;
  onClose: () => void;
  selected: string;
  onSelect: (month: string) => void;
  currentMonth: string;
}

function MonthPickerDialog({ open, onClose, selected, onSelect, currentMonth }: MonthPickerDialogProps) {
  const currentYear = Number(currentMonth.split('-')[0]);
  const [pickerYear, setPickerYear] = useState(() => Number(selected.split('-')[0]));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent showCloseButton={false} className="max-w-xs p-0 gap-0">
        <div className="p-4">
          <DialogTitle className="sr-only">Selecionar mês</DialogTitle>

          <div className="mb-4 flex items-center justify-between">
            <button
              onClick={() => setPickerYear((y) => y - 1)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100"
              aria-label="Ano anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-slate-900">{pickerYear}</span>
            <button
              onClick={() => setPickerYear((y) => y + 1)}
              disabled={pickerYear >= currentYear}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-30"
              aria-label="Próximo ano"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {MONTH_LABELS.map((label, i) => {
              const monthStr = `${pickerYear}-${String(i + 1).padStart(2, '0')}`;
              const isFuture = monthStr > currentMonth;
              const isSelected = monthStr === selected;
              return (
                <button
                  key={label}
                  disabled={isFuture}
                  onClick={() => { onSelect(monthStr); onClose(); }}
                  className={cn(
                    'rounded-lg py-2.5 text-sm font-medium transition-colors',
                    isSelected
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100',
                    isFuture && 'cursor-not-allowed opacity-30',
                  )}
                  aria-pressed={isSelected}
                  aria-disabled={isFuture}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PunchcardHistoryPage() {
  const { activeAgreement } = useAgreementStore();
  const { data: collects = [], isLoading, isError } = usePunchcardCollects(activeAgreement?.uuid);
  const { data: agreementInfo = null } = useAgreementInfo(activeAgreement?.uuid);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedCollect, setSelectedCollect] = useState<Collect | null>(null);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const navigate = useNavigate();

  const currentMonth = format(new Date(), 'yyyy-MM');
  const isCurrentMonthSelected = selectedMonth === currentMonth;

  const collectsByDate = useCollectsByDate(collects);
  const monthStats = useMonthlyStats(collects, selectedMonth);

  const monthLabel = format(parseISO(`${selectedMonth}-01T12:00:00`), 'MMMM yyyy', { locale: ptBR });

  const sortedDates = Object.keys(collectsByDate)
    .filter((d) => d.startsWith(selectedMonth))
    .sort((a, b) => b.localeCompare(a));

  const today = format(new Date(), 'yyyy-MM-dd');

  function goToPrevMonth() {
    const [y, m] = selectedMonth.split('-').map(Number);
    setSelectedMonth(format(new Date(y, (m ?? 1) - 2, 1), 'yyyy-MM'));
  }

  function goToNextMonth() {
    if (isCurrentMonthSelected) return;
    const [y, m] = selectedMonth.split('-').map(Number);
    setSelectedMonth(format(new Date(y, m ?? 1, 1), 'yyyy-MM'));
  }

  if (isLoading) {
    return (
      <div role="status" aria-label="Carregando histórico" className="space-y-3 px-4 py-4">
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-28 w-full rounded-2xl" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-6 mt-6">
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          Erro ao carregar histórico de pontos. Tente novamente.
        </div>
        <Button onClick={() => navigate('/punchcard')}>Registrar ponto</Button>
      </div>
    );
  }

  if (collects.length === 0) {
    return (
      <div className="flex flex-col items-center px-6 py-16 text-center">
        <p className="text-base font-semibold text-slate-900">
          Nenhum registro de ponto encontrado
        </p>
        <p className="mt-1 text-sm text-slate-500 mb-8">
          Registre seu primeiro ponto para começar a ver o histórico
        </p>
        <Button onClick={() => navigate('/punchcard')} aria-label="Registrar ponto">
          Registrar ponto
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white px-6 pb-4 pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-base font-semibold text-slate-900">Histórico de pontos</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/punchcard/calendar')}
              className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Ver calendário mensal"
            >
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              onClick={() => navigate('/punchcard')}
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-colors"
              aria-label="Registrar novo ponto"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <button
            onClick={goToPrevMonth}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100"
            aria-label="Mês anterior"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            onClick={() => setMonthPickerOpen(true)}
            className="text-sm font-semibold capitalize text-slate-900 hover:text-slate-600 transition-colors px-2 py-1 rounded-lg hover:bg-slate-100"
            aria-label="Selecionar mês e ano"
          >
            {monthLabel}
          </button>
          <button
            onClick={goToNextMonth}
            disabled={isCurrentMonthSelected}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-30"
            aria-label="Próximo mês"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="space-y-3 px-4 py-4">
        <Card aria-label="Estatísticas do mês">
          <CardContent>
            <p className="mb-0.5 text-xs font-medium text-slate-500">Horas trabalhadas</p>
            <p className="text-xl font-bold text-slate-900">
              {formatDayTotal(monthStats.totalWorkedMinutes)}
            </p>
          </CardContent>
        </Card>

        {sortedDates.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Nenhum registro neste mês.</p>
        ) : (
          sortedDates.map((date) => {
            const dayCollects = collectsByDate[date] ?? [];
            return (
              <DayTimelineCard
                key={date}
                date={date}
                dayLabel={getDayLabel(date)}
                dateLabel={getDateLabel(date)}
                collects={dayCollects}
                isToday={date === today}
                totalLabel={calcDayTotalLabel(dayCollects)}
                onCollectClick={setSelectedCollect}
                onCollectPeekEnd={() => setSelectedCollect(null)}
              />
            );
          })
        )}
      </div>

      <CollectDetailDialog
        collect={selectedCollect}
        agreementInfo={agreementInfo}
        agreementUuid={activeAgreement?.uuid}
        open={selectedCollect !== null}
        onOpenChange={(open) => { if (!open) setSelectedCollect(null); }}
      />

      <MonthPickerDialog
        open={monthPickerOpen}
        onClose={() => setMonthPickerOpen(false)}
        selected={selectedMonth}
        onSelect={setSelectedMonth}
        currentMonth={currentMonth}
      />
    </div>
  );
}
